import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { ArrowLeft, Mail, Calendar, BookOpen, Award, MessageSquare, CheckCircle2, XCircle } from "lucide-react";

export default function InstructorStudent() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/students/${id}`);
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Could not load student");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500">Loading student…</div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500" data-testid="instructor-student-error">
          {error || "Student not found."}
        </div>
      </div>
    );
  }

  const overallPct =
    data.total_hours > 0 ? Math.round((data.hours_completed / data.total_hours) * 100) : 0;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="instructor-student-page">
        <Link
          to="/instructor"
          className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6"
          data-testid="instructor-student-back"
        >
          <ArrowLeft className="w-4 h-4" /> Cohort
        </Link>

        <div className="overline mb-2"><span className="red-bar" />Student</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mb-3" data-testid="instructor-student-name">
          {data.full_name}
        </h1>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-600 font-mono mb-8">
          <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {data.email}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Joined {new Date(data.joined).toLocaleDateString()}</span>
          {data.paid ? (
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-green-100 text-green-800 px-2 py-0.5">ENROLLED</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-zinc-100 text-zinc-600 px-2 py-0.5">UNPAID</span>
          )}
        </div>

        {/* Top stats */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          <div className="card-tech p-5">
            <div className="overline"><span className="red-bar" />Progress</div>
            <div className="font-heading text-3xl font-bold tracking-tight mt-1">{overallPct}%</div>
            <div className="text-xs text-zinc-500 font-mono mt-1">{data.hours_completed}h / {data.total_hours}h</div>
          </div>
          <div className="card-tech p-5">
            <div className="overline"><span className="red-bar" />Lessons</div>
            <div className="font-heading text-3xl font-bold tracking-tight mt-1">{data.lessons_completed_count}</div>
            <div className="text-xs text-zinc-500 font-mono mt-1">completed</div>
          </div>
          <div className="card-tech p-5">
            <div className="overline"><span className="red-bar" />Final Exam</div>
            <div className="font-heading text-3xl font-bold tracking-tight mt-1">
              {data.final_exam_best ? `${data.final_exam_best.percentage}%` : "—"}
            </div>
            <div className="text-xs text-zinc-500 font-mono mt-1">
              {data.final_exam_best
                ? data.final_exam_best.passed
                  ? "PASSED"
                  : "Not yet"
                : "Not attempted"}
            </div>
          </div>
          <div className="card-tech p-5">
            <div className="overline"><span className="red-bar" />AI Tutor</div>
            <div className="font-heading text-3xl font-bold tracking-tight mt-1">{data.tutor_questions}</div>
            <div className="text-xs text-zinc-500 font-mono mt-1">{data.tutor_sessions} sessions</div>
          </div>
        </div>

        {/* Certificate */}
        {data.certificate && (
          <div className="card-tech p-6 mb-10 border-red-200 bg-red-50/40" data-testid="instructor-student-cert">
            <div className="flex items-center gap-3 mb-2">
              <Award className="w-5 h-5 text-red-600" />
              <div className="overline"><span className="red-bar" />Certificate issued</div>
            </div>
            <div className="font-mono text-sm text-zinc-700">{data.certificate.id}</div>
            <div className="text-xs text-zinc-500 font-mono">
              Issued {new Date(data.certificate.issued_at).toLocaleDateString()} · {data.certificate.percentage}%
            </div>
          </div>
        )}

        {/* Modules */}
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-red-600" /> Module progress
        </h2>
        <div className="space-y-3 mb-12">
          {data.module_progress.map((m) => (
            <div key={m.id} className="card-tech p-4" data-testid={`instructor-module-${m.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-red-600">{m.id.toUpperCase()}</div>
                  <div className="font-heading font-bold tracking-tight">{m.title}</div>
                </div>
                <div className="font-mono text-xs text-zinc-700">
                  {m.hours_completed}h / {m.hours}h · {m.percent}%
                </div>
              </div>
              <div className="h-1.5 bg-zinc-100">
                <div className="h-full bg-red-600" style={{ width: `${m.percent}%` }} />
              </div>
            </div>
          ))}
        </div>

        {/* Quiz attempts */}
        <h2 className="font-heading text-2xl font-bold tracking-tight mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-red-600" /> Quiz attempts
        </h2>
        {data.quiz_attempts.length === 0 ? (
          <div className="text-zinc-500 font-mono text-sm">No quiz attempts yet.</div>
        ) : (
          <div className="border-2 border-zinc-200 overflow-x-auto" data-testid="instructor-student-quizzes">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-950 text-white text-left font-mono text-[11px] uppercase tracking-widest">
                  <th className="px-4 py-3">Quiz</th>
                  <th className="px-4 py-3">Score</th>
                  <th className="px-4 py-3">%</th>
                  <th className="px-4 py-3">Result</th>
                  <th className="px-4 py-3">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {[...data.quiz_attempts].reverse().map((a, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="px-4 py-3">
                      <div className="font-semibold">{a.quiz_title}</div>
                      <div className="font-mono text-[11px] text-zinc-500">{a.quiz_id}</div>
                    </td>
                    <td className="px-4 py-3 font-mono">{a.score} / {a.total}</td>
                    <td className="px-4 py-3 font-mono font-bold">{a.percentage}%</td>
                    <td className="px-4 py-3">
                      {a.passed ? (
                        <span className="inline-flex items-center gap-1 text-green-700 font-mono text-xs">
                          <CheckCircle2 className="w-3.5 h-3.5" /> PASSED
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-600 font-mono text-xs">
                          <XCircle className="w-3.5 h-3.5" /> FAILED
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-500">
                      {new Date(a.submitted_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
